import { c as _c } from "react/compiler-runtime";
import { add, setMilliseconds, startOfHour, sub } from 'date-fns';
import { useContext, useEffect, useMemo, useState } from 'react';
import NumberFormat from 'react-number-format';
import useSWR from 'swr';
import { useTheme } from 'styled-components';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { OutpostAttemptListParams } from '../../../../../../../typings/outpost/Attempt.interface';
import { OutpostAttemptView } from '../../../../../../../typings/View.interface';
import { deepMergeFilters, extractFromArray } from '../../../../utils';
import { resolveRelativeDate } from '../../../../utils/date';
import Badge from '../../../common/base/Badge';
import Button, { ButtonGroup } from '../../../common/base/Button';
import Divider from '../../../common/base/Divider';
import Icon from '../../../common/base/Icon';
import Skeleton from '../../../common/base/Skeleton';
import Text from '../../../common/base/Text';
import { StatusDot } from '../../../common/base/StatusDot';
import Tooltip from '../../../common/base/Tooltip';
import EmptyState, { StyledEmptyStateWrapper } from '../../../common/EmptyState';
import ErrorBoundary from '../../../common/helpers/ErrorBoundary';
import { StyledCentered } from '../../../common/helpers/StyledCentered';
import { Div } from '../../../common/helpers/StyledUtils';
import { useOutpostMetricsQuery, OutpostMetricsQueryParams } from '../../../hooks/useOutpostMetricsQuery';
import { FilterFormValues, outpost_attempt_list_filters as filter_components, outpost_date_filter_component } from '../../../common/Filters/FilterComponents';
import Filters, { FiltersContext } from '../../../common/Filters/Filters';
import { TableProvider } from '../../../contexts/TableContext';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { useIsScrolled } from '../../../hooks/useIsScrolled';
import useSearchQuery from '../../../hooks/useSearchQuery';
import { StyledViewContent, StyledViewLayout, StyledViewNav, StyledViewNavActions, StyledViewNavHeader, StyledViewNavSecondary, ViewTitle } from '../../../layouts/ViewLayout';
import { DateFilter } from '../../DashboardScene/Events/Histogram';
import SuggestedColumns from '../../DashboardScene/Requests/SuggestedColumns';
import { ViewsContext } from '../../DashboardScene/ViewsContext';
import Histogram, { ATTEMPTS_STATUS_DIMENSION_CONFIGS } from '../Events/Histogram';
import AttemptSidebar from './AttemptSidebar';
import AttemptsList from './AttemptsList';
import useRetryAttempt from './useRetryAttempt';
import { DEFAULT_DATE, OutpostAttemptQueryParams, OUTPOST_ATTEMPT_INCLUDES, buildOutpostAttemptListParams, extractAttemptFiltersFromQuery, extractAttemptFiltersFromView } from './utils';
const filters_initial_state = extractAttemptFiltersFromQuery({
  date: DEFAULT_DATE
});
const POLL_NEW_ATTEMPTS_INTERVAL = 5000;
const AttemptsView: React.FC<{
  current_view?: OutpostAttemptView;
}> = t0 => {
  const $ = _c(246);
  const {
    current_view
  } = t0;
  const {
    query,
    updateSearchQuery
  } = useSearchQuery();
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    has_scrolled,
    scrolled_element_ref
  } = useIsScrolled();
  const {
    views,
    createView,
    updateView,
    deleteView,
    renameView,
    duplicateView
  } = useContext(ViewsContext);
  const theme = useTheme();
  const [ignore_current_view, setIgnoreCurrentView] = useState(false);
  const [poll_new_attempts_ready, setPollNewAttemptsReady] = useState(false);
  const current_page = query.current_page ? parseInt(query.current_page, 10) : 1;
  let t1;
  if ($[0] !== query.selected_attempt_id) {
    t1 = extractFromArray(query.selected_attempt_id);
    $[0] = query.selected_attempt_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const selected_attempt_id = t1 as string;
  let t2;
  if ($[2] !== query.selected_history_attempt_id) {
    t2 = extractFromArray(query.selected_history_attempt_id);
    $[2] = query.selected_history_attempt_id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selected_history_attempt_id = t2 as string;
  let new_filters;
  if ($[4] !== query) {
    new_filters = extractAttemptFiltersFromQuery(query);
    if (!query.date) {
      let t3;
      let t4;
      let t5;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        const {
          min,
          max
        } = resolveRelativeDate(DEFAULT_DATE);
        t3 = DEFAULT_DATE;
        t4 = min?.toISOString();
        t5 = max?.toISOString();
        $[6] = t3;
        $[7] = t4;
        $[8] = t5;
      } else {
        t3 = $[6];
        t4 = $[7];
        t5 = $[8];
      }
      let t6;
      if ($[9] !== t4 || $[10] !== t5) {
        t6 = {
          relative: t3,
          min: t4,
          max: t5
        };
        $[9] = t4;
        $[10] = t5;
        $[11] = t6;
      } else {
        t6 = $[11];
      }
      new_filters.date = t6;
    }
    $[4] = query;
    $[5] = new_filters;
  } else {
    new_filters = $[5];
  }
  if (current_view && !ignore_current_view) {
    const should_use_query = Object.values(filter_components).some(value => query[value.filter_key as keyof OutpostAttemptQueryParams] !== undefined);
    if (!should_use_query) {
      let t3;
      if ($[12] !== current_view) {
        t3 = extractAttemptFiltersFromView(current_view);
        $[12] = current_view;
        $[13] = t3;
      } else {
        t3 = $[13];
      }
      const view_filters = t3;
      let t4;
      if ($[14] !== new_filters.next || $[15] !== new_filters.prev || $[16] !== view_filters) {
        t4 = {
          ...filters_initial_state,
          ...view_filters,
          next: new_filters.next,
          prev: new_filters.prev
        };
        $[14] = new_filters.next;
        $[15] = new_filters.prev;
        $[16] = view_filters;
        $[17] = t4;
      } else {
        t4 = $[17];
      }
      new_filters = t4;
    }
  }
  const filters = new_filters;
  let t3;
  bb0: {
    if ("dynamic_columns" in query) {
      t3 = query.dynamic_columns === null ? undefined : query.dynamic_columns;
      break bb0;
    }
    if (current_view && !ignore_current_view && current_view.dynamic_columns) {
      t3 = current_view.dynamic_columns;
      break bb0;
    }
    t3 = undefined;
  }
  const dynamic_columns = t3;
  let t4;
  if ($[18] !== filters) {
    t4 = buildOutpostAttemptListParams(filters);
    $[18] = filters;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  const api_filters = t4;
  let t5;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = APIMethodKeys.outpost.tenants.list({
      limit: 1
    });
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== HookdeckAPI.outpost.tenants) {
    t6 = () => HookdeckAPI.outpost.tenants.list({
      limit: 1
    });
    $[21] = HookdeckAPI.outpost.tenants;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const {
    data: tenants_response
  } = useSWR(t5, t6);
  const has_tenants = !tenants_response || tenants_response.count > 0;
  let t7;
  if ($[23] !== api_filters) {
    t7 = APIMethodKeys.outpost.attempts.list(api_filters);
    $[23] = api_filters;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== HookdeckAPI.outpost.attempts || $[26] !== api_filters) {
    t8 = () => HookdeckAPI.outpost.attempts.list(api_filters);
    $[25] = HookdeckAPI.outpost.attempts;
    $[26] = api_filters;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      revalidateOnFocus: false
    };
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const {
    data: attempts_response,
    isLoading: is_loading,
    mutate: mutateAttempts
  } = useSWR(t7, t8, t9);
  let attempts;
  let selected_attempt;
  let t10;
  if ($[29] !== attempts_response?.models || $[30] !== selected_attempt_id) {
    attempts = attempts_response?.models || [];
    let t11;
    if ($[34] !== selected_attempt_id) {
      t11 = attempt => attempt.id === selected_attempt_id;
      $[34] = selected_attempt_id;
      $[35] = t11;
    } else {
      t11 = $[35];
    }
    selected_attempt = attempts.find(t11);
    t10 = selected_attempt?.event_id ? APIMethodKeys.outpost.attempts.list({
      event_id: selected_attempt.event_id,
      destination_id: selected_attempt.destination_id,
      include: OUTPOST_ATTEMPT_INCLUDES
    }) : null;
    $[29] = attempts_response?.models;
    $[30] = selected_attempt_id;
    $[31] = attempts;
    $[32] = selected_attempt;
    $[33] = t10;
  } else {
    attempts = $[31];
    selected_attempt = $[32];
    t10 = $[33];
  }
  const selected_attempt_history_key = t10;
  let t11;
  if ($[36] !== HookdeckAPI.outpost.attempts || $[37] !== selected_attempt?.destination_id || $[38] !== selected_attempt?.event_id) {
    t11 = () => HookdeckAPI.outpost.attempts.list({
      event_id: selected_attempt?.event_id,
      destination_id: selected_attempt?.destination_id,
      include: OUTPOST_ATTEMPT_INCLUDES
    });
    $[36] = HookdeckAPI.outpost.attempts;
    $[37] = selected_attempt?.destination_id;
    $[38] = selected_attempt?.event_id;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      refreshInterval: 5000
    };
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  const {
    data: selected_attempt_history_response,
    mutate: mutateSelectedAttemptHistory
  } = useSWR(selected_attempt?.event_id && selected_attempt_history_key, t11, t12);
  let t13;
  if ($[41] !== attempts || $[42] !== selected_attempt_history_response?.models || $[43] !== selected_history_attempt_id) {
    t13 = selected_attempt_history_response?.models.find(attempt_1 => attempt_1.id === selected_history_attempt_id) || attempts.find(attempt_0 => attempt_0.id === selected_history_attempt_id);
    $[41] = attempts;
    $[42] = selected_attempt_history_response?.models;
    $[43] = selected_history_attempt_id;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  const selected_history_attempt = t13;
  const sidebar_attempt_id = selected_history_attempt_id || selected_attempt_id;
  const sidebar_attempt = selected_history_attempt || selected_attempt;
  let t14;
  if ($[45] !== mutateAttempts || $[46] !== mutateSelectedAttemptHistory) {
    t14 = async () => {
      await Promise.all([mutateAttempts(), mutateSelectedAttemptHistory?.()]);
    };
    $[45] = mutateAttempts;
    $[46] = mutateSelectedAttemptHistory;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  const {
    retryAttempt,
    retrying_attempt_id
  } = useRetryAttempt(t14);
  filters.date?.relative;
  let t15;
  bb1: {
    const poll_after_timestamp = filters.date?.relative && !attempts_response?.pagination.prev ? attempts_response?.models[0]?.time : null;
    if (!poll_after_timestamp) {
      t15 = undefined;
      break bb1;
    }
    let t16;
    if ($[48] !== poll_after_timestamp) {
      t16 = new Date(new Date(poll_after_timestamp).getTime() + 1000).toISOString();
      $[48] = poll_after_timestamp;
      $[49] = t16;
    } else {
      t16 = $[49];
    }
    let t17;
    if ($[50] !== t16) {
      t17 = {
        gt: t16
      };
      $[50] = t16;
      $[51] = t17;
    } else {
      t17 = $[51];
    }
    let t18;
    if ($[52] !== api_filters || $[53] !== t17) {
      t18 = {
        ...api_filters,
        time: t17,
        limit: 10,
        next: undefined,
        prev: undefined
      };
      $[52] = api_filters;
      $[53] = t17;
      $[54] = t18;
    } else {
      t18 = $[54];
    }
    t15 = t18;
  }
  const poll_new_attempts_filters = t15;
  let t16;
  let t17;
  if ($[55] !== poll_new_attempts_filters) {
    t16 = () => {
      if (!poll_new_attempts_filters) {
        setPollNewAttemptsReady(false);
        return;
      }
      const timeout_id = window.setTimeout(() => {
        setPollNewAttemptsReady(true);
      }, POLL_NEW_ATTEMPTS_INTERVAL);
      return () => window.clearTimeout(timeout_id);
    };
    t17 = [poll_new_attempts_filters];
    $[55] = poll_new_attempts_filters;
    $[56] = t16;
    $[57] = t17;
  } else {
    t16 = $[56];
    t17 = $[57];
  }
  useEffect(t16, t17);
  const should_poll_new_attempts = poll_new_attempts_filters && poll_new_attempts_ready;
  let t18;
  if ($[58] !== poll_new_attempts_filters || $[59] !== should_poll_new_attempts) {
    t18 = should_poll_new_attempts && APIMethodKeys.outpost.attempts.list(poll_new_attempts_filters);
    $[58] = poll_new_attempts_filters;
    $[59] = should_poll_new_attempts;
    $[60] = t18;
  } else {
    t18 = $[60];
  }
  let t19;
  if ($[61] !== HookdeckAPI.outpost.attempts || $[62] !== poll_new_attempts_filters) {
    t19 = () => HookdeckAPI.outpost.attempts.list(poll_new_attempts_filters);
    $[61] = HookdeckAPI.outpost.attempts;
    $[62] = poll_new_attempts_filters;
    $[63] = t19;
  } else {
    t19 = $[63];
  }
  let t20;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = {
      refreshInterval: POLL_NEW_ATTEMPTS_INTERVAL,
      revalidateOnFocus: true
    };
    $[64] = t20;
  } else {
    t20 = $[64];
  }
  const {
    data: new_attempts_response
  } = useSWR(t18, t19, t20);
  const has_next_results = !!new_attempts_response && new_attempts_response.models.length > 0;
  let metrics_filters;
  let stats_end_date;
  let stats_start_date;
  let t21;
  let t22;
  let t23;
  if ($[65] !== filters.date || $[66] !== filters.destination_id || $[67] !== filters.event_id || $[68] !== filters.status || $[69] !== filters.tenant_id || $[70] !== filters.topic) {
    const now = new Date();
    const end_date = setMilliseconds(filters.date?.max ? new Date(filters.date.max) : add(startOfHour(now), {
      hours: 1
    }), 0);
    const start_date = setMilliseconds(filters.date?.min ? new Date(filters.date.min) : sub(add(startOfHour(now), {
      hours: 1
    }), {
      days: 1
    }), 0);
    [stats_start_date, stats_end_date] = [start_date, end_date];
    let t24;
    if ($[77] !== filters.tenant_id) {
      t24 = filters.tenant_id?.length ? {
        tenant_id: filters.tenant_id
      } : {};
      $[77] = filters.tenant_id;
      $[78] = t24;
    } else {
      t24 = $[78];
    }
    let t25;
    if ($[79] !== filters.topic) {
      t25 = filters.topic?.length ? {
        topic: filters.topic
      } : {};
      $[79] = filters.topic;
      $[80] = t25;
    } else {
      t25 = $[80];
    }
    let t26;
    if ($[81] !== filters.status) {
      t26 = filters.status ? {
        status: filters.status
      } : {};
      $[81] = filters.status;
      $[82] = t26;
    } else {
      t26 = $[82];
    }
    let t27;
    if ($[83] !== filters.destination_id) {
      t27 = filters.destination_id ? {
        destination_id: filters.destination_id
      } : {};
      $[83] = filters.destination_id;
      $[84] = t27;
    } else {
      t27 = $[84];
    }
    let t28;
    if ($[85] !== filters.event_id) {
      t28 = filters.event_id ? {
        event_id: filters.event_id
      } : {};
      $[85] = filters.event_id;
      $[86] = t28;
    } else {
      t28 = $[86];
    }
    let t29;
    if ($[87] !== t24 || $[88] !== t25 || $[89] !== t26 || $[90] !== t27 || $[91] !== t28) {
      t29 = {
        ...t24,
        ...t25,
        ...t26,
        ...t27,
        ...t28
      };
      $[87] = t24;
      $[88] = t25;
      $[89] = t26;
      $[90] = t27;
      $[91] = t28;
      $[92] = t29;
    } else {
      t29 = $[92];
    }
    metrics_filters = t29;
    t23 = "attempts";
    t21 = stats_start_date.toISOString();
    t22 = stats_end_date.toISOString();
    $[65] = filters.date;
    $[66] = filters.destination_id;
    $[67] = filters.event_id;
    $[68] = filters.status;
    $[69] = filters.tenant_id;
    $[70] = filters.topic;
    $[71] = metrics_filters;
    $[72] = stats_end_date;
    $[73] = stats_start_date;
    $[74] = t21;
    $[75] = t22;
    $[76] = t23;
  } else {
    metrics_filters = $[71];
    stats_end_date = $[72];
    stats_start_date = $[73];
    t21 = $[74];
    t22 = $[75];
    t23 = $[76];
  }
  let t24;
  if ($[93] !== t21 || $[94] !== t22) {
    t24 = {
      start: t21,
      end: t22
    };
    $[93] = t21;
    $[94] = t22;
    $[95] = t24;
  } else {
    t24 = $[95];
  }
  let t25;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = ["count"];
    $[96] = t25;
  } else {
    t25 = $[96];
  }
  let t26;
  if ($[97] !== metrics_filters || $[98] !== t23 || $[99] !== t24) {
    t26 = {
      endpoint: t23,
      time: t24,
      measures: t25,
      filters: metrics_filters
    };
    $[97] = metrics_filters;
    $[98] = t23;
    $[99] = t24;
    $[100] = t26;
  } else {
    t26 = $[100];
  }
  const count_query = t26;
  const {
    raw_data: count_data
  } = useOutpostMetricsQuery(count_query);
  const attempts_count = count_data?.[0]?.["Attempts.count"] ? parseInt(count_data[0]["Attempts.count"], 10) : undefined;
  let t27;
  if ($[101] !== updateSearchQuery) {
    t27 = id => updateSearchQuery({
      selected_attempt_id: id || undefined
    }, {
      remove_keys: id ? ["selected_history_attempt_id"] : ["selected_attempt_id", "selected_history_attempt_id"]
    });
    $[101] = updateSearchQuery;
    $[102] = t27;
  } else {
    t27 = $[102];
  }
  const onAttemptSelected = t27;
  let t28;
  if ($[103] !== updateSearchQuery) {
    t28 = id_0 => updateSearchQuery({
      selected_history_attempt_id: id_0
    });
    $[103] = updateSearchQuery;
    $[104] = t28;
  } else {
    t28 = $[104];
  }
  const onHistoryAttemptSelected = t28;
  let t29;
  if ($[105] !== attempts || $[106] !== onAttemptSelected || $[107] !== selected_attempt_id) {
    t29 = () => {
      if (!selected_attempt_id || !attempts.length) {
        return;
      }
      const current_index = attempts.findIndex(attempt_2 => attempt_2.id === selected_attempt_id);
      if (current_index > 0) {
        onAttemptSelected(attempts[current_index - 1].id);
      }
    };
    $[105] = attempts;
    $[106] = onAttemptSelected;
    $[107] = selected_attempt_id;
    $[108] = t29;
  } else {
    t29 = $[108];
  }
  const onPrevAttempt = t29;
  let t30;
  if ($[109] !== attempts || $[110] !== onAttemptSelected || $[111] !== selected_attempt_id) {
    t30 = () => {
      if (!selected_attempt_id || !attempts.length) {
        return;
      }
      const current_index_0 = attempts.findIndex(attempt_3 => attempt_3.id === selected_attempt_id);
      if (current_index_0 < attempts.length - 1) {
        onAttemptSelected(attempts[current_index_0 + 1].id);
      }
    };
    $[109] = attempts;
    $[110] = onAttemptSelected;
    $[111] = selected_attempt_id;
    $[112] = t30;
  } else {
    t30 = $[112];
  }
  const onNextAttempt = t30;
  let t31;
  if ($[113] !== current_view || $[114] !== dynamic_columns || $[115] !== filters.date || $[116] !== selected_attempt_id || $[117] !== selected_history_attempt_id || $[118] !== updateSearchQuery) {
    t31 = new_filters_0 => {
      if (current_view) {
        setIgnoreCurrentView(true);
      }
      updateSearchQuery({
        ...new_filters_0,
        date: filters.date?.relative ? filters.date.relative : filters.date,
        selected_attempt_id,
        selected_history_attempt_id,
        dynamic_columns,
        current_page: undefined
      }, {
        remove_keys: ["next", "prev", "current_page"],
        replace: true
      });
    };
    $[113] = current_view;
    $[114] = dynamic_columns;
    $[115] = filters.date;
    $[116] = selected_attempt_id;
    $[117] = selected_history_attempt_id;
    $[118] = updateSearchQuery;
    $[119] = t31;
  } else {
    t31 = $[119];
  }
  const onFilterChanged = t31;
  let t32;
  if ($[120] !== filters || $[121] !== selected_attempt_id || $[122] !== selected_history_attempt_id || $[123] !== updateSearchQuery) {
    t32 = date => {
      if (!date.relative) {
        delete date.relative;
      }
      updateSearchQuery({
        ...filters,
        date: date.relative ? date.relative : date,
        selected_attempt_id,
        selected_history_attempt_id
      }, {
        remove_keys: ["next", "prev", "current_page"]
      });
    };
    $[120] = filters;
    $[121] = selected_attempt_id;
    $[122] = selected_history_attempt_id;
    $[123] = updateSearchQuery;
    $[124] = t32;
  } else {
    t32 = $[124];
  }
  const setDate = t32;
  let t33;
  if ($[125] !== createView || $[126] !== dynamic_columns || $[127] !== filters.date) {
    t33 = values => {
      let view_filters_0 = filter_components.reduce((object, t34) => {
        const {
          form_name,
          formatForDB,
          isActive
        } = t34;
        if (!isActive(values[form_name])) {
          return object;
        }
        const formatted = formatForDB(values[form_name]);
        if (!formatted) {
          return object;
        }
        return deepMergeFilters(object, formatted);
      }, {});
      if (filters.date && outpost_date_filter_component.isActive(filters.date)) {
        const formatted_date = outpost_date_filter_component.formatForDB(filters.date);
        if (formatted_date) {
          view_filters_0 = deepMergeFilters(view_filters_0, formatted_date);
        }
      }
      createView(view_filters_0, dynamic_columns || []);
    };
    $[125] = createView;
    $[126] = dynamic_columns;
    $[127] = filters.date;
    $[128] = t33;
  } else {
    t33 = $[128];
  }
  const handleCreateViewWithValues = t33;
  let t34;
  if ($[129] !== current_view || $[130] !== dynamic_columns || $[131] !== filters.date || $[132] !== updateView) {
    t34 = values_0 => {
      let view_filters_1 = filter_components.reduce((object_0, t35) => {
        const {
          form_name: form_name_0,
          formatForDB: formatForDB_0,
          isActive: isActive_0
        } = t35;
        if (!isActive_0(values_0[form_name_0])) {
          return object_0;
        }
        const formatted_0 = formatForDB_0(values_0[form_name_0]);
        if (!formatted_0) {
          return object_0;
        }
        return deepMergeFilters(object_0, formatted_0);
      }, {});
      if (filters.date && outpost_date_filter_component.isActive(filters.date)) {
        const formatted_date_0 = outpost_date_filter_component.formatForDB(filters.date);
        if (formatted_date_0) {
          view_filters_1 = deepMergeFilters(view_filters_1, formatted_date_0);
        }
      }
      if (current_view) {
        updateView(current_view.id, view_filters_1, dynamic_columns || []);
      }
    };
    $[129] = current_view;
    $[130] = dynamic_columns;
    $[131] = filters.date;
    $[132] = updateView;
    $[133] = t34;
  } else {
    t34 = $[133];
  }
  const handleUpdateViewWithValues = t34;
  let t35;
  if ($[134] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = () => {
      setIgnoreCurrentView(false);
    };
    $[134] = t35;
  } else {
    t35 = $[134];
  }
  let t36;
  if ($[135] !== current_view) {
    t36 = [current_view];
    $[135] = current_view;
    $[136] = t36;
  } else {
    t36 = $[136];
  }
  useEffect(t35, t36);
  let t37;
  if ($[137] !== current_view || $[138] !== deleteView || $[139] !== duplicateView || $[140] !== dynamic_columns || $[141] !== filters.date || $[142] !== handleCreateViewWithValues || $[143] !== handleUpdateViewWithValues || $[144] !== renameView || $[145] !== views) {
    t37 = t38 => {
      const {
        values: values_1
      } = t38;
      const filters_active_count = filter_components.reduce((count, filter) => {
        if (filter.isActive(values_1[filter.form_name])) {
          count++;
        }
        return count;
      }, 0);
      const has_active_filters = filters_active_count > 0 || filters.date && outpost_date_filter_component.isActive(filters.date) && filters.date.relative !== DEFAULT_DATE || dynamic_columns && dynamic_columns.length > 0;
      if (!current_view) {
        if (views && views.count > 249) {
          return <Tooltip align="right" tooltip="Views limit reached"><Button disabled={true} outline={true} icon="save">Save view</Button></Tooltip>;
        }
        return <Button outline={true} disabled={!has_active_filters} onClick={() => handleCreateViewWithValues(values_1)} icon="save">Save view</Button>;
      }
      return <ButtonGroup small={true} more_options={[{
        label: "Save as new view",
        disabled: views && views.count > 249,
        tooltip: views && views.count > 249 ? "Views limit reached" : undefined,
        onClick: () => handleCreateViewWithValues(values_1)
      }, {
        label: "Rename view",
        onClick: () => renameView(current_view.id)
      }, {
        label: "Duplicate view",
        onClick: () => duplicateView(current_view.id)
      }, {
        label: "Delete view",
        danger: true,
        onClick: () => deleteView(current_view.id)
      }]}><Button outline={true} onClick={() => handleUpdateViewWithValues(values_1)} disabled={!has_active_filters} icon="save">Save view</Button></ButtonGroup>;
    };
    $[137] = current_view;
    $[138] = deleteView;
    $[139] = duplicateView;
    $[140] = dynamic_columns;
    $[141] = filters.date;
    $[142] = handleCreateViewWithValues;
    $[143] = handleUpdateViewWithValues;
    $[144] = renameView;
    $[145] = views;
    $[146] = t37;
  } else {
    t37 = $[146];
  }
  const SaveViewButton = t37;
  let t38;
  if ($[147] !== dynamic_columns) {
    t38 = {
      dynamic_columns
    };
    $[147] = dynamic_columns;
    $[148] = t38;
  } else {
    t38 = $[148];
  }
  let t39;
  if ($[149] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = {
      align: "center",
      justify: "space-between",
      gap: 2
    };
    $[149] = t39;
  } else {
    t39 = $[149];
  }
  const t40 = current_view?.name ? `${current_view.name} deliveries` : "Event Deliveries";
  let t41;
  if ($[150] !== t40) {
    t41 = <ViewTitle title={t40} tooltip="Delivery attempts represent each Outpost delivery made for an event. Use filters to inspect delivery outcomes by tenant, destination, topic, or event." />;
    $[150] = t40;
    $[151] = t41;
  } else {
    t41 = $[151];
  }
  let t42;
  if ($[152] !== attempts_count || $[153] !== count_data) {
    t42 = count_data === undefined ? <Skeleton variant="square" h={{
      px: 20
    }} /> : attempts_count !== undefined && <Badge neutral={true}><NumberFormat renderText={_temp} displayType="text" value={attempts_count} thousandSeparator="," /></Badge>;
    $[152] = attempts_count;
    $[153] = count_data;
    $[154] = t42;
  } else {
    t42 = $[154];
  }
  let t43;
  if ($[155] !== t41 || $[156] !== t42) {
    t43 = <Div flex={t39}>{t41}{t42}</Div>;
    $[155] = t41;
    $[156] = t42;
    $[157] = t43;
  } else {
    t43 = $[157];
  }
  const t44 = is_loading ? "loading" : "reload";
  const t45 = is_loading || !filters.date?.relative;
  let t46;
  if ($[158] !== mutateAttempts || $[159] !== updateSearchQuery) {
    t46 = () => {
      updateSearchQuery({}, {
        remove_keys: ["prev", "next", "selected_attempt_id", "selected_history_attempt_id", "current_page"]
      });
      mutateAttempts();
    };
    $[158] = mutateAttempts;
    $[159] = updateSearchQuery;
    $[160] = t46;
  } else {
    t46 = $[160];
  }
  let t47;
  if ($[161] !== has_next_results) {
    t47 = has_next_results && <StatusDot color="primary" m={{
      l: 2
    }} />;
    $[161] = has_next_results;
    $[162] = t47;
  } else {
    t47 = $[162];
  }
  let t48;
  if ($[163] !== t44 || $[164] !== t45 || $[165] !== t46 || $[166] !== t47) {
    t48 = <Button icon={t44} outline={true} disabled={t45} onClick={t46}>Refresh{t47}</Button>;
    $[163] = t44;
    $[164] = t45;
    $[165] = t46;
    $[166] = t47;
    $[167] = t48;
  } else {
    t48 = $[167];
  }
  let t49;
  if ($[168] !== SaveViewButton) {
    t49 = <FiltersContext.Consumer>{t50 => {
        const {
          values: values_2
        } = t50;
        return <SaveViewButton values={values_2} />;
      }}</FiltersContext.Consumer>;
    $[168] = SaveViewButton;
    $[169] = t49;
  } else {
    t49 = $[169];
  }
  let t50;
  if ($[170] !== t48 || $[171] !== t49) {
    t50 = <StyledViewNavActions>{t48}{t49}</StyledViewNavActions>;
    $[170] = t48;
    $[171] = t49;
    $[172] = t50;
  } else {
    t50 = $[172];
  }
  let t51;
  if ($[173] !== t43 || $[174] !== t50) {
    t51 = <StyledViewNavHeader>{t43}{t50}</StyledViewNavHeader>;
    $[173] = t43;
    $[174] = t50;
    $[175] = t51;
  } else {
    t51 = $[175];
  }
  let t52;
  let t53;
  if ($[176] === Symbol.for("react.memo_cache_sentinel")) {
    t52 = {
      align: "flex-start",
      gap: 3
    };
    t53 = {
      x: 6,
      y: 3
    };
    $[176] = t52;
    $[177] = t53;
  } else {
    t52 = $[176];
    t53 = $[177];
  }
  let t54;
  if ($[178] !== filters || $[179] !== setDate || $[180] !== stats_end_date || $[181] !== stats_start_date) {
    t54 = <DateFilter filters={filters} setDate={setDate} start_date={stats_start_date} end_date={stats_end_date} />;
    $[178] = filters;
    $[179] = setDate;
    $[180] = stats_end_date;
    $[181] = stats_start_date;
    $[182] = t54;
  } else {
    t54 = $[182];
  }
  let t55;
  if ($[183] === Symbol.for("react.memo_cache_sentinel")) {
    t55 = <Divider vertical={true} h={{
      px: 28
    }} />;
    $[183] = t55;
  } else {
    t55 = $[183];
  }
  let t56;
  if ($[184] === Symbol.for("react.memo_cache_sentinel")) {
    t56 = <Div w={100}><Filters.Form /></Div>;
    $[184] = t56;
  } else {
    t56 = $[184];
  }
  let t57;
  if ($[185] !== t54) {
    t57 = <StyledViewNavSecondary p={0}><Div flex={t52} p={t53}>{t54}{t55}{t56}</Div></StyledViewNavSecondary>;
    $[185] = t54;
    $[186] = t57;
  } else {
    t57 = $[186];
  }
  let t58;
  if ($[187] !== t51 || $[188] !== t57) {
    t58 = <StyledViewNav>{t51}{t57}</StyledViewNav>;
    $[187] = t51;
    $[188] = t57;
    $[189] = t58;
  } else {
    t58 = $[189];
  }
  let t59;
  let t60;
  if ($[190] === Symbol.for("react.memo_cache_sentinel")) {
    t59 = {
      overflow: "auto",
      height: "calc(100vh - 106px)",
      position: "relative"
    };
    t60 = {
      direction: "column"
    };
    $[190] = t59;
    $[191] = t60;
  } else {
    t59 = $[190];
    t60 = $[191];
  }
  let t61;
  if ($[192] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = {
      x: 6,
      y: 3
    };
    $[192] = t61;
  } else {
    t61 = $[192];
  }
  const t62 = filters as any;
  const t63 = attempts[0]?.time;
  let t64;
  if ($[193] !== metrics_filters || $[194] !== setDate || $[195] !== stats_end_date || $[196] !== stats_start_date || $[197] !== t62 || $[198] !== t63) {
    t64 = <Div p={t61}><Histogram filters={t62} start_date={stats_start_date} end_date={stats_end_date} setDate={setDate} refresh_key={t63} endpoint="attempts" label="Deliveries" metrics_filters={metrics_filters} dimension="status" dimension_configs={ATTEMPTS_STATUS_DIMENSION_CONFIGS} /></Div>;
    $[193] = metrics_filters;
    $[194] = setDate;
    $[195] = stats_end_date;
    $[196] = stats_start_date;
    $[197] = t62;
    $[198] = t63;
    $[199] = t64;
  } else {
    t64 = $[199];
  }
  let t65;
  if ($[200] === Symbol.for("react.memo_cache_sentinel")) {
    t65 = <Divider />;
    $[200] = t65;
  } else {
    t65 = $[200];
  }
  let t66;
  if ($[201] !== attempts) {
    t66 = attempts.length > 0 && <><Div flex={{
        align: "flex-start",
        gap: 3
      }} p={{
        x: 6,
        y: 3
      }}><Div flex={{
          align: "center",
          gap: 2
        }} h={{
          px: 28
        }} m={{
          r: 4.5
        }}><Icon icon="add_column_right" muted={true} /><Text semi={true} muted={true} text_wrap={false}>Suggested columns</Text></Div><Divider vertical={true} /><Div w={100}><SuggestedColumns entity="outpost_attempts" models={attempts} /></Div></Div><Divider /></>;
    $[201] = attempts;
    $[202] = t66;
  } else {
    t66 = $[202];
  }
  let t67;
  if ($[203] !== t64 || $[204] !== t66) {
    t67 = <StyledCentered>{t64}{t65}{t66}</StyledCentered>;
    $[203] = t64;
    $[204] = t66;
    $[205] = t67;
  } else {
    t67 = $[205];
  }
  let t68;
  if ($[206] !== attempts_response || $[207] !== current_page || $[208] !== has_tenants || $[209] !== is_loading || $[210] !== onAttemptSelected || $[211] !== onHistoryAttemptSelected || $[212] !== retryAttempt || $[213] !== retrying_attempt_id || $[214] !== selected_attempt_history_response || $[215] !== selected_attempt_id || $[216] !== selected_history_attempt_id || $[217] !== theme || $[218] !== updateSearchQuery) {
    t68 = !has_tenants && !is_loading ? <StyledEmptyStateWrapper><EmptyState title="No delivery attempts yet" description="Publish an event to one of your tenants with at least one destination to start seeing deliveries." asset={`outpost-attempts-${theme.mode}.png`} asset_position="background" cta={{
        label: "Get started",
        icon: "grid_view",
        to: "/get-started"
      }} /></StyledEmptyStateWrapper> : <ErrorBoundary><AttemptsList attempts_response={attempts_response} history_attempts_response={selected_attempt_history_response} loading={is_loading} selected_attempt_id={selected_attempt_id} selected_history_attempt_id={selected_history_attempt_id} onAttemptSelected={onAttemptSelected} onHistoryAttemptSelected={onHistoryAttemptSelected} onRetryAttempt={retryAttempt} retrying_attempt_id={retrying_attempt_id} current_page={current_page} onNextPage={attempts_response?.pagination?.next ? () => {
        updateSearchQuery({
          next: attempts_response.pagination.next || "",
          prev: undefined,
          current_page: String(current_page + 1)
        });
      } : undefined} onPreviousPage={current_page > 1 ? () => {
        updateSearchQuery({
          prev: attempts_response?.pagination?.prev || undefined,
          next: undefined,
          current_page: String(current_page - 1)
        });
      } : undefined} /></ErrorBoundary>;
    $[206] = attempts_response;
    $[207] = current_page;
    $[208] = has_tenants;
    $[209] = is_loading;
    $[210] = onAttemptSelected;
    $[211] = onHistoryAttemptSelected;
    $[212] = retryAttempt;
    $[213] = retrying_attempt_id;
    $[214] = selected_attempt_history_response;
    $[215] = selected_attempt_id;
    $[216] = selected_history_attempt_id;
    $[217] = theme;
    $[218] = updateSearchQuery;
    $[219] = t68;
  } else {
    t68 = $[219];
  }
  let t69;
  if ($[220] !== scrolled_element_ref || $[221] !== t67 || $[222] !== t68) {
    t69 = <Div style={t59} flex={t60} ref={scrolled_element_ref}>{t67}{t68}</Div>;
    $[220] = scrolled_element_ref;
    $[221] = t67;
    $[222] = t68;
    $[223] = t69;
  } else {
    t69 = $[223];
  }
  let t70;
  if ($[224] !== t58 || $[225] !== t69) {
    t70 = <StyledViewContent>{t58}{t69}</StyledViewContent>;
    $[224] = t58;
    $[225] = t69;
    $[226] = t70;
  } else {
    t70 = $[226];
  }
  let t71;
  if ($[227] !== onNextAttempt || $[228] !== onPrevAttempt || $[229] !== retryAttempt || $[230] !== retrying_attempt_id || $[231] !== sidebar_attempt || $[232] !== sidebar_attempt_id || $[233] !== updateSearchQuery) {
    t71 = sidebar_attempt_id && <AttemptSidebar attempt_id={sidebar_attempt_id} list_attempt={sidebar_attempt} onPrev={onPrevAttempt} onNext={onNextAttempt} onRetryAttempt={retryAttempt} retrying_attempt_id={retrying_attempt_id} onClose={() => updateSearchQuery({}, {
      remove_keys: ["selected_attempt_id", "selected_history_attempt_id"]
    })} />;
    $[227] = onNextAttempt;
    $[228] = onPrevAttempt;
    $[229] = retryAttempt;
    $[230] = retrying_attempt_id;
    $[231] = sidebar_attempt;
    $[232] = sidebar_attempt_id;
    $[233] = updateSearchQuery;
    $[234] = t71;
  } else {
    t71 = $[234];
  }
  let t72;
  if ($[235] !== t70 || $[236] !== t71) {
    t72 = <StyledViewLayout>{t70}{t71}</StyledViewLayout>;
    $[235] = t70;
    $[236] = t71;
    $[237] = t72;
  } else {
    t72 = $[237];
  }
  let t73;
  if ($[238] !== filters || $[239] !== onFilterChanged || $[240] !== t72) {
    t73 = <Filters.Provider route="event-deliveries" filters={filters} components={filter_components} onFilterChanged={onFilterChanged}>{t72}</Filters.Provider>;
    $[238] = filters;
    $[239] = onFilterChanged;
    $[240] = t72;
    $[241] = t73;
  } else {
    t73 = $[241];
  }
  let t74;
  if ($[242] !== has_scrolled || $[243] !== t38 || $[244] !== t73) {
    t74 = <TableProvider initialConfig={t38} has_scrolled={has_scrolled}>{t73}</TableProvider>;
    $[242] = has_scrolled;
    $[243] = t38;
    $[244] = t73;
    $[245] = t74;
  } else {
    t74 = $[245];
  }
  return t74;
};
export default AttemptsView;
function _temp(v) {
  return v;
}